import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import {
  pageTitleClass,
  pageSubtitleClass,
  cardClass,
  cardBodyClass,
  badgeClass,
  formLabelClass,
  formControlClass,
  btnPrimaryClass,
  btnSecondaryClass,
  btnDangerClass,
  alertErrorClass,
  alertInfoClass,
  spinnerClass,
  tableClass,
  tableHeadClass,
  tableHeaderCellClass,
  tableBodyClass,
  tableRowClass,
  tableCellClass,
  tableActionsCellClass,
} from "../../dashboardStyles";
import {
  GetCompanyInvitesAction,
  CreateCompanyInviteAction,
  RevokeCompanyInviteAction,
  GetAdminsAction,
  getUserInfoFromToken,
} from "../../../../store/action";
import { UserRole } from "../../../../constants/roles";
import { notify, notifySaveError } from "../../../../utils/notify";

const STATUS_BADGE = {
  active: "bg-brand-50 text-brand-700",
  revoked: "bg-ink-100 text-ink-500",
  expired: "bg-amber-50 text-amber-700",
  exhausted: "bg-amber-50 text-amber-700",
};

/** A link is worth copying or withdrawing only while it still works. */
const isLive = (invite) => invite.status === "active";

/**
 * Builds the link to hand over. Absolute, because it is going into an email or
 * a chat message someone writes themselves — a relative path would be useless
 * the moment it leaves this page.
 */
export const inviteLinkFor = (token, origin) =>
  `${origin}/signup?invite=${encodeURIComponent(token)}`;

/**
 * Onboarding links for new businesses.
 *
 * The link itself is the boring half. What it buys is the assignment: every
 * company that signs up through an admin's link is that admin's without anyone
 * remembering to say so, which is otherwise a manual step only a system owner
 * can perform and nobody thinks of.
 *
 * Links are reusable, so one URL can go in an email signature or a campaign and
 * bring in any number of businesses. Reach is bounded by the expiry date, an
 * optional cap, and revocation — the lever that ends a campaign.
 *
 * Delivery is deliberately not automated: staff copy the link into whatever
 * conversation they are already having. That is also why the token is shown
 * again every time this page loads rather than once at creation.
 */
const Invitations = () => {
  const { t, i18n } = useTranslation();

  const role = getUserInfoFromToken()?.role;
  const isSystemOwner = role === UserRole.SYSTEM_OWNER;

  const [invites, setInvites] = useState([]);
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  const [invitedEmail, setInvitedEmail] = useState("");
  const [note, setNote] = useState("");
  const [assignTo, setAssignTo] = useState("");
  const [maxUses, setMaxUses] = useState("");
  const [creating, setCreating] = useState(false);

  // Which link was last copied, so the button can confirm it happened. Cleared
  // on the next copy so two rows never both claim to be the copied one.
  const [copiedId, setCopiedId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setFailed(false);
    try {
      const [rows, adminList] = await Promise.all([
        GetCompanyInvitesAction(),
        isSystemOwner ? GetAdminsAction() : Promise.resolve([]),
      ]);
      setInvites(Array.isArray(rows) ? rows : []);
      setAdmins(Array.isArray(adminList) ? adminList : []);
    } catch (err) {
      setFailed(true);
    } finally {
      setLoading(false);
    }
  }, [isSystemOwner]);

  useEffect(() => {
    load();
  }, [load]);

  const create = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      await CreateCompanyInviteAction({
        invitedEmail: invitedEmail.trim() || null,
        note: note.trim() || null,
        // Ignored by the API for an admin, who always invites on their own
        // behalf. Sent only so a system owner can direct the company somewhere.
        assignToAdminId: assignTo === "" ? null : Number(assignTo),
        // Blank means unlimited, which is what an open campaign wants.
        maxUses: maxUses === "" ? null : Number(maxUses),
      });
      setInvitedEmail("");
      setNote("");
      setAssignTo("");
      setMaxUses("");
      notify.success(t("Dashboard.Platform.invitations.created"));
      await load();
    } catch (err) {
      notifySaveError(err, t("Dashboard.Platform.invitations.errors.create"));
    } finally {
      setCreating(false);
    }
  };

  const revoke = async (invite) => {
    try {
      await RevokeCompanyInviteAction(invite.id);
      notify.success(t("Dashboard.Platform.invitations.revoked"));
      await load();
    } catch (err) {
      notifySaveError(err, t("Dashboard.Platform.invitations.errors.revoke"));
    }
  };

  const copy = async (invite) => {
    const link = inviteLinkFor(invite.token, window.location.origin);
    try {
      await navigator.clipboard.writeText(link);
      setCopiedId(invite.id);
      notify.success(t("Dashboard.Platform.invitations.copiedHint"));
    } catch (err) {
      // Clipboard access can be refused — an insecure origin, or a browser that
      // wants a permission first. Failing silently would leave someone clicking
      // a button that never does anything, so say so instead. Yellow: nothing
      // is broken, the link just has to be copied by hand.
      notify.warning(t("Dashboard.Platform.invitations.errors.copy"));
    }
  };

  const formatDate = (value) =>
    value ? new Date(value).toLocaleDateString(i18n.language) : "—";

  return (
    <div>
      <div className="mb-6">
        <h1 className={pageTitleClass}>
          {t("Dashboard.Platform.invitations.title")}
        </h1>
        <p className={pageSubtitleClass}>
          {t("Dashboard.Platform.invitations.subtitle")}
        </p>
      </div>

      {failed && (
        <div className={alertErrorClass} role="alert">
          <i className="bi bi-exclamation-triangle" aria-hidden="true" />
          <span>{t("Dashboard.Platform.invitations.loadError")}</span>
        </div>
      )}

      <div className={`${cardClass} mb-6`}>
        <form onSubmit={create} className="flex flex-col gap-3 p-4 md:flex-row md:items-end">
          <div className="min-w-0 flex-1">
            <label className={formLabelClass} htmlFor="invite-email">
              {t("Dashboard.Platform.invitations.form.email")}
            </label>
            <input
              id="invite-email"
              type="email"
              className={formControlClass}
              value={invitedEmail}
              placeholder={t("Dashboard.Platform.invitations.form.emailPlaceholder")}
              onChange={(e) => setInvitedEmail(e.target.value)}
            />
          </div>

          <div className="min-w-0 flex-1">
            <label className={formLabelClass} htmlFor="invite-note">
              {t("Dashboard.Platform.invitations.form.note")}
            </label>
            <input
              id="invite-note"
              type="text"
              className={formControlClass}
              value={note}
              placeholder={t("Dashboard.Platform.invitations.form.notePlaceholder")}
              onChange={(e) => setNote(e.target.value)}
            />
          </div>

          <div className="md:w-32">
            <label className={formLabelClass} htmlFor="invite-max-uses">
              {t("Dashboard.Platform.invitations.form.maxUses")}
            </label>
            <input
              id="invite-max-uses"
              type="number"
              min="1"
              className={formControlClass}
              value={maxUses}
              placeholder={t("Dashboard.Platform.invitations.form.unlimited")}
              onChange={(e) => setMaxUses(e.target.value)}
            />
          </div>

          {isSystemOwner && (
            <div className="md:w-52">
              <label className={formLabelClass} htmlFor="invite-assign">
                {t("Dashboard.Platform.invitations.form.assignTo")}
              </label>
              <select
                id="invite-assign"
                className={formControlClass}
                value={assignTo}
                onChange={(e) => setAssignTo(e.target.value)}
              >
                <option value="">
                  {t("Dashboard.Platform.invitations.form.unassigned")}
                </option>
                {admins.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.firstName} {a.lastName}
                  </option>
                ))}
              </select>
            </div>
          )}

          <button type="submit" className={btnPrimaryClass} disabled={creating}>
            <i className="bi bi-plus-lg" aria-hidden="true" />
            {t("Dashboard.Platform.invitations.form.submit")}
          </button>
        </form>
      </div>

      {/* Says what the link actually does, because it is not obvious from a
          token and it is the entire reason the feature exists. */}
      <div className={`${alertInfoClass} mb-6`}>
        <i className="bi bi-info-circle" aria-hidden="true" />
        <span>
          {isSystemOwner
            ? t("Dashboard.Platform.invitations.explainerOwner")
            : t("Dashboard.Platform.invitations.explainerAdmin")}
        </span>
      </div>

      {loading ? (
        <div className="flex min-h-[30vh] items-center justify-center">
          <div
            role="status"
            aria-label={t("Dashboard.Menu.loading")}
            className={spinnerClass}
          />
        </div>
      ) : invites.length === 0 ? (
        <div className={alertInfoClass}>
          <i className="bi bi-envelope" aria-hidden="true" />
          <span>{t("Dashboard.Platform.invitations.empty")}</span>
        </div>
      ) : (
        <div className={cardClass}>
          <div className={cardBodyClass}>
            <table className={tableClass}>
              <thead className={tableHeadClass}>
                <tr>
                  <th className={tableHeaderCellClass}>
                    {t("Dashboard.Platform.invitations.columns.invited")}
                  </th>
                  <th className={tableHeaderCellClass}>
                    {t("Dashboard.Platform.invitations.columns.status")}
                  </th>
                  <th className={tableHeaderCellClass}>
                    {t("Dashboard.Platform.invitations.columns.uses")}
                  </th>
                  <th className={tableHeaderCellClass}>
                    {t("Dashboard.Platform.invitations.columns.assignedTo")}
                  </th>
                  <th className={tableHeaderCellClass}>
                    {t("Dashboard.Platform.invitations.columns.result")}
                  </th>
                  <th className={tableHeaderCellClass}>
                    {t("Dashboard.Platform.invitations.columns.expires")}
                  </th>
                  <th className={tableHeaderCellClass}>
                    <span className="sr-only">
                      {t("Dashboard.Platform.invitations.columns.actions")}
                    </span>
                  </th>
                </tr>
              </thead>
              <tbody className={tableBodyClass}>
                {invites.map((invite) => (
                  <tr key={invite.id} className={tableRowClass}>
                    <td
                      className={tableCellClass}
                      data-label={t(
                        "Dashboard.Platform.invitations.columns.invited",
                      )}
                    >
                      <span className="flex flex-col">
                        <span className="font-medium text-ink-900">
                          {invite.invitedEmail ||
                            t("Dashboard.Platform.invitations.noEmail")}
                        </span>
                        {invite.note && (
                          <span className="text-xs text-ink-500">
                            {invite.note}
                          </span>
                        )}
                      </span>
                    </td>
                    <td
                      className={tableCellClass}
                      data-label={t(
                        "Dashboard.Platform.invitations.columns.status",
                      )}
                    >
                      <span
                        className={`${badgeClass} ${STATUS_BADGE[invite.status] ?? "bg-ink-100 text-ink-500"}`}
                      >
                        {t(
                          `Dashboard.Platform.invitations.status.${invite.status}`,
                        )}
                      </span>
                    </td>
                    <td
                      className={tableCellClass}
                      data-label={t(
                        "Dashboard.Platform.invitations.columns.uses",
                      )}
                    >
                      <span className="flex flex-col items-end md:items-start">
                        <span className="font-medium text-ink-900">
                          {invite.maxUses == null
                            ? t("Dashboard.Platform.invitations.usesUnlimited", {
                                count: invite.useCount,
                              })
                            : t("Dashboard.Platform.invitations.usesCapped", {
                                count: invite.useCount,
                                max: invite.maxUses,
                              })}
                        </span>
                        {/* Signups and companies diverge whenever someone
                            registers and never finishes the setup wizard — a
                            campaign with clicks and no companies has a problem
                            after the click, not before it. */}
                        {invite.useCount !== invite.companyCount && (
                          <span className="text-xs text-ink-400">
                            {t("Dashboard.Platform.invitations.companiesOf", {
                              count: invite.companyCount,
                            })}
                          </span>
                        )}
                      </span>
                    </td>
                    <td
                      className={tableCellClass}
                      data-label={t(
                        "Dashboard.Platform.invitations.columns.assignedTo",
                      )}
                    >
                      {invite.assignToAdminName ??
                        t("Dashboard.Platform.invitations.form.unassigned")}
                    </td>
                    <td
                      className={tableCellClass}
                      data-label={t(
                        "Dashboard.Platform.invitations.columns.result",
                      )}
                    >
                      {invite.redemptions?.length ? (
                        <span className="flex flex-col gap-0.5">
                          {invite.redemptions.slice(0, 3).map((r) => (
                            <span key={r.email} className="text-xs">
                              {r.organizationName ?? (
                                <span className="text-ink-400">
                                  {t(
                                    "Dashboard.Platform.invitations.signedUpNoCompany",
                                  )}
                                </span>
                              )}
                            </span>
                          ))}
                          {invite.redemptions.length > 3 && (
                            <span className="text-xs text-ink-400">
                              {t("Dashboard.Platform.invitations.andMore", {
                                count: invite.redemptions.length - 3,
                              })}
                            </span>
                          )}
                        </span>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td
                      className={tableCellClass}
                      data-label={t(
                        "Dashboard.Platform.invitations.columns.expires",
                      )}
                    >
                      {formatDate(invite.expiresAt)}
                    </td>
                    <td className={tableActionsCellClass}>
                      <span className="flex flex-wrap justify-end gap-2">
                        {isLive(invite) && (
                          <>
                            <button
                              type="button"
                              className={btnSecondaryClass}
                              onClick={() => copy(invite)}
                            >
                              <i
                                className={`bi ${copiedId === invite.id ? "bi-check-lg" : "bi-clipboard"}`}
                                aria-hidden="true"
                              />
                              {copiedId === invite.id
                                ? t("Dashboard.Platform.invitations.copied")
                                : t("Dashboard.Platform.invitations.copy")}
                            </button>
                            <button
                              type="button"
                              className={btnDangerClass}
                              onClick={() => revoke(invite)}
                            >
                              {t("Dashboard.Platform.invitations.revoke")}
                            </button>
                          </>
                        )}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default Invitations;
